import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { darkTheme } from '../../../lib/tokens';
import type { TutorUsageBreakdown, VoiceSessionsSummary } from './api';
import {
  formatEur,
  formatHmm,
  formatTokens,
  formatUsd,
  sourceColor,
  sourceLabel,
} from './format';

interface Props {
  tutor: TutorUsageBreakdown;
  voice: VoiceSessionsSummary;
  dailyDays: number;
}

// KI-Tutor-Kosten in einer Sektion:
//  - Gesamtkosten (EUR primaer, USD sekundaer)
//  - Donut nach Source (KI-Tutor vs. Mündliche Pruefung vs. ...)
//  - Daily-Bar-Chart (Tokens pro Tag, letzte N Tage)
//  - Voice-Sessions als separater Sub-Block (Anzahl, Dauer, Cents inkl. ElevenLabs)

export function TutorCostSection({ tutor, voice, dailyDays }: Props): JSX.Element {
  const hasUsage = tutor.totals.callCount > 0;

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-text-primary">KI-Tutor-Kosten</h3>
        <span
          className="text-[11px] text-text-tertiary"
          title="Anthropic-Preise Stand 05/2026, USD→EUR fix @0.93. Voice inkl. ElevenLabs-Streaming."
        >
          inkl. ElevenLabs · Konversion fix @0,93
        </span>
      </div>

      {!hasUsage && voice.count === 0 ? (
        <p className="text-xs text-text-tertiary">Noch keine KI-Calls dieses Nutzers.</p>
      ) : (
        <div className="space-y-4">
          <Totals tutor={tutor} voice={voice} />
          <div className="grid gap-4 lg:grid-cols-2">
            <SourceDonut tutor={tutor} />
            <DailyBars tutor={tutor} dailyDays={dailyDays} />
          </div>
          <VoiceBlock voice={voice} />
        </div>
      )}
    </div>
  );
}

function Totals({
  tutor,
  voice,
}: {
  tutor: TutorUsageBreakdown;
  voice: VoiceSessionsSummary;
}): JSX.Element {
  const totalEur = tutor.totals.costEur + voice.totalCostEur;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Stat
        label="Gesamtkosten"
        primary={formatEur(totalEur)}
        secondary={`Tutor ${formatUsd(tutor.totals.costUsd)} + Voice ${formatEur(voice.totalCostEur)}`}
      />
      <Stat
        label="Tokens (Anthropic)"
        primary={formatTokens(tutor.totals.totalTokens)}
        secondary={`${tutor.totals.callCount} Calls`}
      />
      <Stat
        label="Voice-Sessions"
        primary={String(voice.count)}
        secondary={`${voice.last30DaysCount} in 30 Tagen`}
      />
      <Stat
        label="Voice-Minuten"
        primary={formatHmm(voice.totalDurationSec)}
        secondary={
          voice.count > 0
            ? `Ø ${Math.round(voice.totalDurationSec / voice.count / 60)} min/Session`
            : '—'
        }
      />
    </div>
  );
}

function Stat({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary?: string;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-3"
      style={{
        background: darkTheme.colors.bgElevated,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <div className="mt-1 text-base font-bold tabular text-text-primary">{primary}</div>
      {secondary && <div className="mt-0.5 text-[10px] text-text-tertiary">{secondary}</div>}
    </div>
  );
}

function SourceDonut({ tutor }: { tutor: TutorUsageBreakdown }): JSX.Element {
  const data = tutor.bySource.map((s) => ({
    name: sourceLabel(s.source),
    rawSource: s.source,
    value: s.costUsd,
    tokens: s.totalTokens,
    callCount: s.callCount,
    costEur: s.costEur,
  }));

  if (data.length === 0) {
    return (
      <div
        className="flex h-[180px] items-center justify-center rounded-md"
        style={{ background: darkTheme.colors.bgElevated }}
      >
        <p className="text-xs text-text-tertiary">Kein Verbrauch</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        Verbrauch nach Feature
      </div>
      <div className="flex items-center gap-3">
        <ResponsiveContainer width={120} height={120}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={32}
              outerRadius={56}
              paddingAngle={1}
              dataKey="value"
              isAnimationActive={false}
            >
              {data.map((d) => (
                <Cell
                  key={d.rawSource}
                  fill={sourceColor(d.rawSource)}
                  stroke={darkTheme.colors.bgSurface}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: darkTheme.colors.bgElevated,
                border: `1px solid ${darkTheme.colors.borderSubtle}`,
                fontSize: 12,
              }}
              labelStyle={{ color: darkTheme.colors.textSecondary }}
              formatter={(value: number, _key, item) => {
                const payload = item.payload as { tokens: number; callCount: number; costEur: number };
                return [
                  `${formatUsd(value)} · ${formatTokens(payload.tokens)} Tokens · ${payload.callCount} Calls`,
                  '',
                ];
              }}
            />
          </PieChart>
        </ResponsiveContainer>

        <ul className="flex-1 space-y-1.5 text-xs">
          {tutor.bySource.map((s) => (
            <li key={s.source} className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: sourceColor(s.source) }}
                />
                <span className="truncate text-text-primary">{sourceLabel(s.source)}</span>
              </span>
              <span className="shrink-0 text-text-tertiary tabular">
                {formatEur(s.costEur)} · {formatTokens(s.totalTokens)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function DailyBars({
  tutor,
  dailyDays,
}: {
  tutor: TutorUsageBreakdown;
  dailyDays: number;
}): JSX.Element {
  const data = tutor.daily.map((d) => ({
    date: d.date.slice(5),
    tokens: d.totalTokens,
    costEur: d.costEur,
  }));
  const total = tutor.daily.reduce((acc, d) => acc + d.totalTokens, 0);

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-[11px]">
        <span className="font-semibold uppercase tracking-wide text-text-tertiary">
          Tokens pro Tag ({dailyDays} Tage)
        </span>
        <span className="text-text-tertiary">Σ {formatTokens(total)}</span>
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={darkTheme.colors.borderSubtle} />
          <XAxis
            dataKey="date"
            stroke={darkTheme.colors.textTertiary}
            fontSize={9}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke={darkTheme.colors.textTertiary}
            fontSize={9}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: darkTheme.colors.bgElevated,
              border: `1px solid ${darkTheme.colors.borderSubtle}`,
              fontSize: 12,
            }}
            labelStyle={{ color: darkTheme.colors.textSecondary }}
            formatter={(value: number, _key, item) => {
              const eur = (item.payload as { costEur: number }).costEur;
              return [`${formatTokens(value)} · ${formatEur(eur)}`, 'Tokens'];
            }}
          />
          <Bar dataKey="tokens" fill={darkTheme.colors.accent} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function VoiceBlock({ voice }: { voice: VoiceSessionsSummary }): JSX.Element {
  if (voice.count === 0) return <></>;
  return (
    <div
      className="rounded-md border p-3 text-xs"
      style={{
        background: darkTheme.colors.bgElevated,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="font-semibold text-text-primary">Voice-Sessions (ElevenLabs + Claude)</div>
      <div className="mt-1 text-text-tertiary">
        {voice.count} Sessions ({voice.last30DaysCount} in den letzten 30 Tagen) ·{' '}
        {formatHmm(voice.totalDurationSec)} Gesamtdauer · {formatEur(voice.totalCostEur)} (
        {voice.totalCostCents} ct)
      </div>
    </div>
  );
}
